/**
 * React Query hooks for support tickets (`api/tickets/*`).
 *
 * Query keys come from TICKET_QUERY_KEYS to isolate student from staff caching.
 * Mutations invalidate TICKET_QUERY_KEYS.root to refresh lists, details, and activity stamps.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  OPERATIONAL_QUERY_OPTIONS,
  STUDENT_QUERY_OPTIONS,
  TICKET_QUERY_KEYS,
} from "@/lib/api/query-options";
import {
  addTicketReply,
  assignTicket,
  changeTicketPriority,
  changeTicketStatus,
  createTicket,
  fetchAssignableStaff,
  fetchMyTickets,
  fetchStudentTicketTopics,
  fetchTicket,
  fetchTickets,
  uploadTicketAttachment,
  type AddTicketReplyInput,
  type CreateTicketInput,
  type MyTicketsQuery,
  type TicketPriority,
  type TicketQuery,
  type TicketStatus,
} from "@/lib/api/tickets";

function useInvalidateTickets() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: TICKET_QUERY_KEYS.root });
}

export function useStudentTicketTopics() {
  return useQuery({
    queryKey: TICKET_QUERY_KEYS.studentTopics,
    queryFn: () => fetchStudentTicketTopics(),
    ...STUDENT_QUERY_OPTIONS,
  });
}

export function useMyTickets(params: MyTicketsQuery = {}) {
  return useQuery({
    queryKey: TICKET_QUERY_KEYS.myList(params),
    queryFn: () => fetchMyTickets(params),
    ...STUDENT_QUERY_OPTIONS,
  });
}

export function useStaffTickets(params: TicketQuery = {}) {
  return useQuery({
    queryKey: TICKET_QUERY_KEYS.staffList(params),
    queryFn: () => fetchTickets(params),
    ...OPERATIONAL_QUERY_OPTIONS,
  });
}

export function useTicket(ticketId: string) {
  return useQuery({
    queryKey: TICKET_QUERY_KEYS.detail(ticketId),
    queryFn: () => fetchTicket(ticketId),
    enabled: Boolean(ticketId),
    ...OPERATIONAL_QUERY_OPTIONS,
  });
}

export function useCreateTicket() {
  const invalidateTickets = useInvalidateTickets();
  return useMutation({
    mutationFn: (input: CreateTicketInput) => createTicket(input),
    onSuccess: () => invalidateTickets(),
  });
}

export function useAddTicketReply(ticketId: string) {
  const invalidateTickets = useInvalidateTickets();
  return useMutation({
    mutationFn: (input: AddTicketReplyInput) => addTicketReply(ticketId, input),
    onSuccess: () => invalidateTickets(),
  });
}

export function useChangeTicketStatus(ticketId: string) {
  const invalidateTickets = useInvalidateTickets();
  return useMutation({
    mutationFn: (status: TicketStatus) => changeTicketStatus(ticketId, status),
    onSuccess: () => invalidateTickets(),
  });
}

export function useChangeTicketPriority(ticketId: string) {
  const invalidateTickets = useInvalidateTickets();
  return useMutation({
    mutationFn: (priority: TicketPriority) => changeTicketPriority(ticketId, priority),
    onSuccess: () => invalidateTickets(),
  });
}

export function useAssignTicket(ticketId: string) {
  const invalidateTickets = useInvalidateTickets();
  return useMutation({
    mutationFn: (assignedToUserId: string | null) => assignTicket(ticketId, assignedToUserId),
    onSuccess: () => invalidateTickets(),
  });
}

export function useUploadTicketAttachment() {
  return useMutation({
    mutationFn: (file: File) => uploadTicketAttachment(file),
  });
}

export function useAssignableStaff(ticketId: string, enabled = true) {
  return useQuery({
    queryKey: TICKET_QUERY_KEYS.assignableStaff(ticketId),
    queryFn: () => fetchAssignableStaff(ticketId),
    enabled: enabled && Boolean(ticketId),
    ...OPERATIONAL_QUERY_OPTIONS,
  });
}
